export interface FileUploadError {
  code:
    | "file-invalid-type"
    | "file-too-large"
    | "file-too-small"
    | "too-many-files";
  message: string;
}

export interface FileUploadRejection {
  file: File;
  errors: FileUploadError[];
}

export interface ValidateFilesOptions {
  accept?: string;
  disabled: boolean;
  fileTypeName?: string;
  maxFiles?: number;
  maxSize?: number;
  minSize?: number;
}

export const isFileAccepted = (file: File, accept?: string) => {
  if (!accept) return true;

  const list = accept.split(",").map((item) => item.trim().toLowerCase());
  const name = file.name.toLowerCase();
  const type = (file.type || "").toLowerCase();

  return list.some((item) => {
    if (item.startsWith(".")) {
      return name.endsWith(item);
    }
    if (item.endsWith("/*")) {
      const prefix = item.slice(0, -1);
      return type.startsWith(prefix);
    }
    return type === item;
  });
};

export const validateFiles = (
  files: File[],
  {
    accept,
    disabled,
    fileTypeName,
    maxFiles,
    maxSize,
    minSize,
  }: ValidateFilesOptions,
) => {
  if (disabled) return { accepted: [], rejected: [] };

  const rejected: FileUploadRejection[] = [];
  const accepted: File[] = [];

  if (maxFiles && files.length > maxFiles) {
    const error: FileUploadError = {
      code: "too-many-files",
      message: `Please select no more than ${maxFiles} file${
        maxFiles === 1 ? "" : "s"
      }.`,
    };
    files.forEach((file) => rejected.push({ file, errors: [error] }));
    return { accepted, rejected };
  }

  files.forEach((file) => {
    const errors: FileUploadError[] = [];

    if (accept && !isFileAccepted(file, accept)) {
      errors.push({
        code: "file-invalid-type",
        message: fileTypeName
          ? `Please select a valid ${fileTypeName} file.`
          : "Invalid file type.",
      });
    }

    if (typeof maxSize === "number" && file.size > maxSize) {
      errors.push({
        code: "file-too-large",
        message: "File is too large.",
      });
    }

    if (typeof minSize === "number" && file.size < minSize) {
      errors.push({
        code: "file-too-small",
        message: "File is too small.",
      });
    }

    if (errors.length > 0) {
      rejected.push({ file, errors });
    } else {
      accepted.push(file);
    }
  });

  return { accepted, rejected };
};

export const formatRejectionMessage = (rejections: FileUploadRejection[]) => {
  if (rejections.length === 0) return null;

  const [firstRejection] = rejections;
  const [firstError] = firstRejection.errors;

  if (!firstError) return null;

  if (firstError.code === "too-many-files") {
    return firstError.message;
  }

  if (rejections.length === 1) {
    return `${firstRejection.file.name}: ${firstError.message}`;
  }

  const uniqueMessages = new Set(
    rejections.flatMap((rejection) =>
      rejection.errors.map((error) => error.message),
    ),
  );
  if (uniqueMessages.size === 1) {
    return `${rejections.length} files rejected. ${Array.from(uniqueMessages)[0]}`;
  }

  return `${rejections.length} files rejected.`;
};
